import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Calendar, HandCoins, Anchor, Heart, Users, Trophy, Dumbbell,
} from "lucide-react";
import { KRAKEN_FULL_LOGO } from "@/components/PublicLayout";
import api from "@/lib/api";

const WAVE_BG = "https://images.unsplash.com/photo-1484387436194-cf7cb70800ce?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85";
const HERO_VIDEO_MP4 = "/video/hero-water.mp4";
const HERO_VIDEO_WEBM = "/video/hero-water.webm";
const HERO_VIDEO_POSTER = "/video/hero-water-poster.jpg";

const krakenDifference = [
  { title: "Athlete Development", icon: Dumbbell,
    text: "Skill-building, baseball IQ, and whole-body development — every practice has a purpose." },
  { title: "Faith", icon: Anchor,
    text: "Anchored in something greater than the game. Players, families, and coaches united by purpose." },
  { title: "Character", icon: Trophy,
    text: "Discipline, accountability, respect. Character is built one rep, one play, one decision at a time." },
  { title: "Brotherhood", icon: Users,
    text: "Teammates first. We compete hard, we win together, and we lift each other up." },
  { title: "Competition", icon: Heart,
    text: "Relentless effort against the best. We chase growth through hard, honest competition." },
];

export default function Home() {
  const [useImageFallback, setUseImageFallback] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (m.matches) setUseImageFallback(true);
  }, []);
  useEffect(() => {
    api.get("/sponsors").then((r) => setSponsors(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* 1. HERO */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden grain" data-testid="hero-section">
        <div className="absolute inset-0">
          {useImageFallback ? (
            <img src={WAVE_BG} alt="" aria-hidden="true"
              className="w-full h-full object-cover opacity-70 hero-ocean" data-testid="hero-ocean-bg" />
          ) : (
            <video
              poster={HERO_VIDEO_POSTER} autoPlay loop muted playsInline preload="auto" aria-hidden="true"
              onError={() => setUseImageFallback(true)}
              className="absolute inset-0 w-full h-full object-cover" data-testid="hero-video-bg"
            >
              <source src={HERO_VIDEO_WEBM} type="video/webm" />
              <source src={HERO_VIDEO_MP4} type="video/mp4" />
            </video>
          )}
          <div className="absolute inset-0 bg-[rgba(2,8,23,0.55)]" data-testid="hero-overlay" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050A15]/40 via-transparent to-[#050A15]/90" />
          <div className="absolute inset-0 wave-bg opacity-25" />
          <div
            className="absolute top-[38%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110vw] h-[110vw] max-w-[900px] max-h-[900px] pointer-events-none hero-shimmer"
            style={{
              background:
                "radial-gradient(circle at center, rgba(0,229,255,0.34) 0%, rgba(0,229,255,0.12) 32%, rgba(0,229,255,0.02) 55%, transparent 70%)",
            }}
            data-testid="hero-shimmer-glow"
          />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 w-full py-24 text-center">
          <div className="relative flex justify-center mb-6" data-testid="hero-logo-wrap">
            <img
              src={KRAKEN_FULL_LOGO}
              alt="Coastal Kraken Baseball"
              className="w-[min(92vw,720px)] h-auto drop-shadow-[0_0_90px_rgba(0,229,255,0.65)]"
              data-testid="hero-logo"
            />
          </div>
          <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight uppercase leading-[0.95]" data-testid="hero-title">
            Anchored In Faith. <span className="text-[#00E5FF]">Relentless</span> In Competition.
          </h1>
          <div className="mt-8 flex flex-wrap gap-3 justify-center" data-testid="hero-ctas">
            <Link to="/tryouts" className="btn-primary" data-testid="cta-join">Join the Kraken <ArrowRight size={16} /></Link>
            <Link to="/schedule" className="btn-ghost" data-testid="cta-schedule"><Calendar size={16} /> View Schedule</Link>
            <Link to="/sponsors" className="btn-ghost" data-testid="cta-sponsor">Become a Sponsor</Link>
          </div>
        </div>
      </section>

      {/* 2. MISSION */}
      <section className="relative py-24 border-y border-white/10 bg-[#0B132B]/50" data-testid="mission-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="eyebrow mb-4">Our Mission</div>
          <p className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase leading-tight tracking-tight">
            Coastal Kraken Baseball exists to <span className="text-[#00E5FF]">develop young athletes</span> into disciplined players, strong teammates, and <span className="text-[#00E5FF]">leaders of character</span> through competitive baseball, faith, service, and hard work.
          </p>
        </div>
      </section>

      {/* 3. CURRENT SPONSORS */}
      <section className="relative py-24 overflow-hidden border-b border-white/10" data-testid="current-sponsors-section">
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            background:
              "radial-gradient(ellipse at 20% 0%, rgba(0,229,255,0.10) 0%, transparent 55%), radial-gradient(ellipse at 80% 100%, rgba(0,229,255,0.08) 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="eyebrow mb-3">Current Sponsors</div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase font-bold tracking-tight leading-[1.05]">
              Thank You <span className="text-[#00E5FF]">Current Sponsors</span>
            </h2>
            <p className="text-slate-300 mt-5 leading-relaxed">
              Thank you to the businesses, families, and community supporters helping make Coastal Kraken Baseball possible.
            </p>
          </div>

          <div className="mt-12">
            {sponsors.length === 0 ? (
              <div
                className="kr-card rounded-2xl p-10 sm:p-14 text-center max-w-2xl mx-auto border border-[#00E5FF]/20"
                style={{ boxShadow: "0 0 60px rgba(0,229,255,0.08)" }}
                data-testid="home-sponsors-empty"
              >
                <Anchor size={32} className="text-[#00E5FF] mx-auto mb-4" />
                <div className="font-display text-2xl sm:text-3xl uppercase tracking-tight">
                  Sponsor recognition <span className="text-[#00E5FF]">coming soon</span>
                </div>
                <p className="text-slate-400 mt-3 text-sm sm:text-base">
                  Be among the first to stand with the Kraken — your name or logo will live right here.
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {sponsors.map((s) => (
                  <div
                    key={s.id}
                    className="kr-card rounded-xl p-5 flex items-center justify-between gap-3 border border-white/10 hover:border-[#00E5FF]/40 transition-colors"
                    data-testid={`home-sponsor-${s.id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{s.name}</div>
                      {s.message && (
                        <div className="text-slate-400 text-xs truncate mt-0.5">{s.message}</div>
                      )}
                    </div>
                    {s.tier && (
                      <span className="text-[10px] uppercase tracking-widest border border-[#00E5FF]/30 text-[#00E5FF] rounded-full px-3 py-1 shrink-0">
                        {s.tier}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-12 text-center max-w-2xl mx-auto">
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
              Interested in supporting local youth baseball? Explore sponsorship opportunities and join the Kraken family.
            </p>
            <Link
              to="/sponsors"
              className="btn-primary inline-flex mt-6"
              data-testid="home-become-sponsor-cta"
            >
              <HandCoins size={16} /> Become a Sponsor <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* 4. TRYOUTS — The Tides Are Turning */}
      <section className="py-20" data-testid="tryouts-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 kr-card rounded-2xl p-12 text-center">
          <div className="eyebrow mb-3">Tryouts Open</div>
          <h3 className="font-display text-3xl sm:text-5xl uppercase tracking-tight">The Tides Are Turning</h3>
          <p className="text-slate-400 mt-4 max-w-2xl mx-auto">
            Submit your tryout interest today. We&apos;ll be in touch with dates, locations, and what to expect.
          </p>
          <Link to="/tryouts" className="btn-primary mt-8 inline-flex" data-testid="bottom-cta-btn">
            Start Tryout Registration <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* 5. KRAKEN DIFFERENCE */}
      <section className="relative py-24 overflow-hidden border-t border-white/10" data-testid="kraken-difference-section">
        <div className="absolute inset-0 opacity-15">
          <img src={WAVE_BG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="eyebrow mb-3">The Kraken Difference</div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase font-bold tracking-tight">
              Five <span className="text-[#00E5FF]">Anchors</span> That Set Us Apart
            </h2>
            <p className="text-slate-300 mt-5 leading-relaxed">
              The Coastal Kraken way is more than wins. It&apos;s how we train, compete, and represent our families and community.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-12">
            {krakenDifference.map((k) => (
              <div
                key={k.title}
                className="kr-card rounded-xl p-6 flex flex-col"
                data-testid={`kraken-diff-${k.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <k.icon size={30} className="text-[#00E5FF] mb-4" />
                <div className="font-display text-lg uppercase leading-tight">{k.title}</div>
                <p className="text-slate-400 text-sm mt-3 leading-relaxed">{k.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
